// src/auth/ProtectedByPathModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Navigate, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { ROUTE_RULES, PROMO_UNLOCKS } from "./gateConfig";
import { NASCAR_ALL } from "./plans";
import AccessDeniedModal from "../components/AccessDeniedModal.jsx";
import SportsPagePreview from "../components/SportsPagePreview";

const EMPTY_ALLOW = [];
const PUBLIC_PATHS = ["/", "/login", "/signup", "/pricing", "/account", "/dashboard", "/discord"];
const planLabel = (plan) => plan ? plan.replaceAll("_", " ") : "";
const pathMatches = (prefix, path) => path === prefix ||
  (prefix !== "/" && path.startsWith(prefix + "/"));
const sportFromPath = (path) => ["nfl", "nascar", "mlb", "nba"]
  .find((sport) => pathMatches(`/${sport}`, path))?.toUpperCase() || null;
const isProToolPath = (path) => path.includes("/optimizer") || path.includes("/contest-sim");
const matchCupArticleSlug = (path) => path.match(/^\/nascar\/cup\/articles\/([^/]+)\/?$/);
const isCupArticlesIndex = (path) => /^\/nascar\/cup\/articles\/?$/.test(path);

let cupIndexPromise = null;
async function isFreeCupArticlePath(path) {
  const match = matchCupArticleSlug(path);
  if (!match) return false;
  if (!cupIndexPromise) {
    cupIndexPromise = fetch("/data/nascar/cup/articles/index.json", { cache: "no-cache" })
      .then((response) => response.ok ? response.json() : [])
      .then((data) => Array.isArray(data) ? data : [])
      .catch(() => []);
  }
  const articles = await cupIndexPromise;
  return Boolean(articles.find((article) => article?.slug === match[1])?.free);
}

export default function ProtectedByPathModal({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const sport = sportFromPath(pathname);
  const isArticle = Boolean(matchCupArticleSlug(pathname));
  const rule = useMemo(() => ROUTE_RULES.find((item) => pathname.startsWith(item.prefix)), [pathname]);
  const baseAllowList = rule?.allow ?? EMPTY_ALLOW;
  const alwaysPublic = PUBLIC_PATHS.some((path) => pathMatches(path, pathname)) ||
    Object.entries(PROMO_UNLOCKS).some(([path, open]) => open && pathMatches(path, pathname)) ||
    isCupArticlesIndex(pathname);
  const [access, setAccess] = useState({ pathname: null, loading: true,
    allowed: false, authed: false, profile: null, error: false });
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    // Supabase can confirm SIGNED_IN again when a browser tab regains focus.
    // Only a changed identity (or an explicit account update) needs a new
    // access check. Rechecking every event unmounts children and loses state.
    let currentUserId;
    const invalidateAccess = () => {
      // Clear the previous account's decision before checking a new identity.
      setAccess({ pathname: null, loading: true, allowed: false,
        authed: false, profile: null, error: false });
      setRevision((value) => value + 1);
    };
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      const nextUserId = session?.user?.id ?? null;
      if (event === "INITIAL_SESSION") {
        // The access effect below already performs the initial check.
        currentUserId = nextUserId;
        return;
      }
      if (event === "SIGNED_OUT") {
        currentUserId = null;
        invalidateAccess();
        return;
      }
      if (nextUserId !== currentUserId) {
        currentUserId = nextUserId;
        invalidateAccess();
      } else if (event === "USER_UPDATED") {
        // Refresh this account's access in the background.
        setRevision((value) => value + 1);
      }
    });
    return () => data?.subscription?.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const finish = (result) => {
      if (!cancelled) setAccess({ pathname, loading: false, allowed: false,
        authed: false, profile: null, error: false, ...result });
    };
    // Keep the resolved page mounted during same-account revalidation.
    // Route changes and identity changes still require a blocking check.
    setAccess((previous) => previous.pathname === pathname && !previous.loading
      ? previous
      : { pathname, loading: true, allowed: false, authed: false,
          profile: null, error: false });
    (async () => {
      try {
        if (alwaysPublic || (isArticle && await isFreeCupArticlePath(pathname))) {
          finish({ allowed: true });
          return;
        }
        const { data: authData, error: authError } = await supabase.auth.getUser();
        if (authError && authError.name !== "AuthSessionMissingError") throw authError;
        const user = authData?.user;
        if (!user) { finish({}); return; }
        const { data: profile, error: profileError } = await supabase
          .from("profiles").select("plan,status").eq("id", user.id).single();
        // A missing profile has no paid access; other lookup failures offer retry.
        if (profileError && profileError.code !== "PGRST116") throw profileError;
        const allow = isArticle ? NASCAR_ALL : baseAllowList;
        const active = profile?.status === "active" || profile?.status === "trialing";
        finish({ authed: true, profile,
          allowed: Boolean(active && (allow.length === 0 || allow.includes(profile?.plan))) });
      } catch {
        finish({ error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [pathname, alwaysPublic, isArticle, baseAllowList, revision]);

  // Never mount a new route with the previous route's access decision.
  // No tool is mounted until the access check finishes.
  if (access.pathname !== pathname || access.loading) {
    return (
      <div role="status" aria-live="polite" style={{
        minHeight: "70vh", padding: 32, boxSizing: "border-box",
        background: "#080d1b", color: "#a8b6cc",
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 12, fontSize: 14,
      }}>
        <style>{`
          @keyframes sports-access-spin { to { transform: rotate(360deg); } }
          .sports-access-spinner { animation: sports-access-spin .85s linear infinite; }
          @media (prefers-reduced-motion: reduce) {
            .sports-access-spinner { animation: none; }
          }
        `}</style>
        <span className="sports-access-spinner" aria-hidden="true" style={{
          width: 19, height: 19, flexShrink: 0, borderRadius: "50%",
          border: "2px solid #243047", borderTopColor: "#60a5fa",
        }} />
        <span>Loading your page…</span>
      </div>
    );
  }
  if (access.error) {
    return <div role="alert" style={{ minHeight: "50vh", padding: 32, textAlign: "center" }}>
      <p>We couldn’t check your access. Please try again.</p>
      <button type="button" onClick={() => setRevision((value) => value + 1)}>Try again</button>
    </div>;
  }
  if (access.allowed) return children;

  const proTool = isProToolPath(pathname);
  const primaryPlan = sport ? `${sport}_${proTool ? "PRO" : "LITE"}` : "ALL_ACCESS_LITE";
  const allAccessPlan = sport && !proTool ? "ALL_ACCESS_LITE" : "ALL_ACCESS_PRO";
  const buyHref = (plan) => `/pricing?plan=${encodeURIComponent(plan.toLowerCase())}&from=${encodeURIComponent(pathname)}`;
  const actions = [
    { label: `Get ${planLabel(primaryPlan)}`, href: buyHref(primaryPlan) },
    { label: `Get ${planLabel(allAccessPlan)}`, href: buyHref(allAccessPlan) },
    { label: "See all plans", href: "/pricing" },
  ];
  const title = sport
    ? `Unlock ${sport} ${proTool ? "PRO" : "LITE or PRO"}` : "Upgrade required";
  const currentPlan = access.profile?.plan
    ? `Your current plan: ${planLabel(access.profile.plan)} (${access.profile.status || "inactive"}). ` : "";
  const description = `${currentPlan}Get full access to this page and its tools.`;

  // Preserve the existing paid article modal; never preview a full paid article.
  if (sport && !isArticle) {
    return <SportsPagePreview key={pathname} pathname={pathname} title={title}
      description={description} actions={actions} authed={access.authed}>
      {children}
    </SportsPagePreview>;
  }
  if (!access.authed && !isArticle) return <Navigate to="/login" replace />;
  return <AccessDeniedModal open onClose={() => navigate("/", { replace: true })}
    title={title} description={description} actions={actions} />;
}
